import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Filter, Music } from 'lucide-react';
import BeatCard from '../components/BeatCard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BeatsPage = () => {
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genres, setGenres] = useState([]);
  const [moods, setMoods] = useState([]);
  const [filters, setFilters] = useState({ genre: '', mood: '', minBpm: '', maxBpm: '' });

  useEffect(() => {
    fetchBeats();
    fetchFilters();
  }, []);

  const fetchBeats = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.genre) params.append('genre', filters.genre);
      if (filters.mood) params.append('mood', filters.mood);
      if (filters.minBpm) params.append('min_bpm', filters.minBpm);
      if (filters.maxBpm) params.append('max_bpm', filters.maxBpm);
      
      const response = await axios.get(`${API}/beats?${params.toString()}`);
      setBeats(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [genresRes, moodsRes] = await Promise.all([
        axios.get(`${API}/beats/genres/list`),
        axios.get(`${API}/beats/moods/list`)
      ]);
      setGenres(genresRes.data);
      setMoods(moodsRes.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchBeats();
  }, [filters]);

  return (
    <div className="min-h-screen pt-24 pb-32 px-6 lg:px-12" data-testid="beats-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>
            <span className="brand-font text-[#D4AF37]">Beat</span> Store
          </h1>
          <p className="text-neutral-500 text-lg">Find your next hit</p>
        </div>

        {/* Filters */}
        <div className="glass-card p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={18} className="text-[#D4AF37]" />
            <span className="font-semibold text-white">Filters</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <select
              value={filters.genre}
              onChange={(e) => setFilters({ ...filters, genre: e.target.value })}
              className="bg-neutral-900 border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white"
              data-testid="genre-filter"
            >
              <option value="">All Genres</option>
              {genres.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <select
              value={filters.mood}
              onChange={(e) => setFilters({ ...filters, mood: e.target.value })}
              className="bg-neutral-900 border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white"
              data-testid="mood-filter"
            >
              <option value="">All Moods</option>
              {moods.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <input
              type="number"
              placeholder="Min BPM"
              value={filters.minBpm}
              onChange={(e) => setFilters({ ...filters, minBpm: e.target.value })}
              className="bg-neutral-900 border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white"
              data-testid="min-bpm-filter"
            />
            <input
              type="number"
              placeholder="Max BPM"
              value={filters.maxBpm}
              onChange={(e) => setFilters({ ...filters, maxBpm: e.target.value })}
              className="bg-neutral-900 border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white"
              data-testid="max-bpm-filter"
            />
          </div>
        </div>

        {/* Beat Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="glass-card p-4 animate-pulse">
                <div className="aspect-square rounded-xl bg-neutral-800 mb-4" />
                <div className="h-5 bg-neutral-800 rounded mb-2 w-3/4" />
                <div className="h-4 bg-neutral-800 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : beats.length === 0 ? (
          <div className="text-center py-20">
            <Music size={48} className="mx-auto mb-4 text-[#D4AF37]/30" />
            <p className="text-neutral-500">No beats found matching your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {beats.map(beat => (
              <BeatCard key={beat.beat_id} beat={beat} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BeatsPage;
